import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, ToggleButtonGroup,
  ToggleButton, Alert, CircularProgress, Chip, Stack, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper,
} from '@mui/material';
import {
  PieChart, Pie, Cell,
  ComposedChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { summaryAPI, transactionsAPI, accountsAPI } from '../services/api';
import { getCategoryColor } from '../constants/categoryColors';
import Navbar from '../components/Navbar';

const PERIODS = [
  { key: '1m',  label: '1M' },
  { key: '3m',  label: '3M' },
  { key: '6m',  label: '6M' },
  { key: 'ytd', label: 'YTD' },
  { key: 'all', label: 'ALL' },
  { key: 'custom', label: 'CUSTOM' },
];

const fmtY = (v) => {
  const a = Math.abs(v);
  if (a >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
  if (a >= 1000)    return '$' + (v / 1000).toFixed(0) + 'k';
  return '$' + v.toFixed(0);
};

const fmtCurrency = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function Home() {
  const [period, setPeriod] = useState('6m');
  const [tab, setTab] = useState('category'); // 'category' | 'trend'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Summary numbers
  const [overview, setOverview] = useState({ total_income: 0, total_expenses: 0, net: 0, transaction_count: 0 });
  // Monthly trend
  const [monthly, setMonthly] = useState([]);
  // Category spending
  const [categories, setCategories] = useState([]);

  // Custom date range
  const [customOpen, setCustomOpen] = useState(false);
  const [customRange, setCustomRange] = useState({ from: '', to: '' });

  // Drilled-down transactions
  const [drillCategory, setDrillCategory] = useState(null);
  const [drillTxns, setDrillTxns] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  // Build API params from period.
  // Always emit from_date (overview + byCategory need it) plus months (monthly endpoint uses it).
  const computeFromDate = useCallback((p) => {
    if (p === 'custom') return customRange.from || undefined;
    if (p === 'ytd')    return new Date().getFullYear() + '-01-01';
    if (p === 'all')    return '1970-01-01';
    const months = { '1m': 1, '3m': 3, '6m': 6 };
    const d = new Date();
    d.setMonth(d.getMonth() - (months[p] || 6));
    return d.toISOString().split('T')[0];
  }, [customRange.from]);

  const buildParams = useCallback(() => {
    const fd = computeFromDate(period);
    const p = {};
    if (fd) p.from_date = fd;
    if (period === 'custom' && customRange.to) p.to_date = customRange.to;
    // only send months for non-date-anchored periods (ytd/all/custom already use from_date)
    if (['1m', '3m', '6m'].includes(period) && !fd) {
      p.months = { '1m': 1, '3m': 3, '6m': 6 }[period];
    }
    return p;
  }, [period, customRange, computeFromDate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = buildParams();
      const [ov, mo, cat] = await Promise.all([
        summaryAPI.overview(params),
        summaryAPI.monthly(params),
        summaryAPI.byCategory(params),
      ]);
      setOverview(ov.data);
      setMonthly(mo.data);
      setCategories(cat.data);
    } catch (err) {
      setError('Failed to load data. ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { loadData(); }, [loadData]);

  // Drill-down when clicking a category
  const handleDrillDown = async (category) => {
    if (category === 'Other') return; // synthetic group
    setDrillCategory(category);
    setDrillLoading(true);
    try {
      const params = buildParams();
      const res = await transactionsAPI.list({
        category,
        transaction_type: 'expense',
        from_date: params.from_date,
        to_date: params.to_date,
        limit: 200,
      });
      setDrillTxns(res.data || []);
    } catch {
      setDrillTxns([]);
    } finally {
      setDrillLoading(false);
    }
  };

  const clearDrill = () => {
    setDrillCategory(null);
    setDrillTxns([]);
  };

  // ── Pie data (group < 5% into "Other") ──
  const totalSpend = categories.reduce((s, c) => s + (c.total || 0), 0);
  const threshold = totalSpend * 0.05;
  const significant = categories.filter(c => c.total >= threshold);
  const otherSum = categories.filter(c => c.total < threshold)
    .reduce((s, c) => s + (c.total || 0), 0);
  const pieData = [
    ...significant.map(c => ({ name: c.category, value: c.total })),
    ...(otherSum > 0 ? [{ name: 'Other', value: parseFloat(otherSum.toFixed(2)) }] : []),
  ];

  return (
    <>
      <Navbar />
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>

        {/* Title + Period pills */}
        <Box display="flex" justifyContent="space-between" alignItems="center"
             mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h4" fontWeight={700}>Home</Typography>
          <ToggleButtonGroup value={period} exclusive
            onChange={(_, v) => {
              if (!v) return;
              if (v === 'custom') { setCustomOpen(true); return; }
              setPeriod(v);
              setDrillCategory(null);
            }}
            size="small">
            {PERIODS.map(p => (
              <ToggleButton key={p.key} value={p.key} sx={{ px: 1.5 }}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {loading && <Box textAlign="center" py={4}><CircularProgress /></Box>}

        {!loading && (
          <>
            {/* ── 4 Stat cards ── */}
            <Grid container spacing={2} mb={3}>
              {[
                { label: 'Total Income',   value: overview.total_income,    color: 'success.main' },
                { label: 'Total Expenses', value: overview.total_expenses,  color: 'error.main' },
                { label: 'Net Savings',    value: overview.net,
                  color: (overview.net || 0) >= 0 ? 'success.main' : 'error.main' },
                { label: 'Transactions',   value: overview.transaction_count, raw: true },
              ].map(({ label, value, color, raw }) => (
                <Grid item xs={6} md={3} key={label}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {label}
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color={color || 'text.primary'}>
                        {raw ? value : fmtCurrency(value)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* ── Tab toggle: By Category / By Trend ── */}
            <Box mb={2}>
              <ToggleButtonGroup value={tab} exclusive
                onChange={(_, v) => v && setTab(v)} size="small">
                <ToggleButton value="category">By Category</ToggleButton>
                <ToggleButton value="trend">By Trend</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* ── Tab content ── */}
            {tab === 'trend' ? (
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Income &amp; Expenses Over Time
                  </Typography>
                  {monthly.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={4}>
                      No data for this period.
                    </Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={monthly}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={fmtY} tick={{ fontSize: 12 }} width={62} />
                        <ReTooltip formatter={fmtCurrency} />
                        <Legend />
                        <Bar dataKey="income"   name="Income"   fill="#4caf50"
                             radius={[4,4,0,0]} opacity={0.85} />
                        <Bar dataKey="expenses" name="Expenses" fill="#f44336"
                             radius={[4,4,0,0]} opacity={0.85} />
                        <Line type="monotone" dataKey="net" name="Net Savings"
                              stroke="#1976d2" strokeWidth={2.5} dot={{ r: 3 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={2}>
                {/* Pie chart */}
                <Grid item xs={12} md={5}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        Spending by Category
                      </Typography>
                      {pieData.length === 0 ? (
                        <Typography color="text.secondary" textAlign="center" py={4}>
                          No expense data for this period.
                        </Typography>
                      ) : (
                        <ResponsiveContainer width="100%" height={340}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              dataKey="value"
                              cx="50%" cy="50%"
                              innerRadius={55} outerRadius={120}
                              onClick={(_, idx) => {
                                if (pieData[idx]) handleDrillDown(pieData[idx].name);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {pieData.map((entry, i) => (
                                <Cell
                                  key={i}
                                  fill={getCategoryColor(entry.name, i)}
                                  opacity={entry.name === 'Other' ? 0.5 : 1}
                                />
                              ))}
                            </Pie>
                            <ReTooltip formatter={fmtCurrency} />
                            <Legend
                              wrapperStyle={{ fontSize: 12, lineHeight: '22px', paddingTop: 8 }}
                              onClick={(entry) => handleDrillDown(entry.value)}
                              style={{ cursor: 'pointer' }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Category table */}
                <Grid item xs={12} md={7}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        Breakdown
                      </Typography>
                      {categories.length === 0 ? (
                        <Typography color="text.secondary" textAlign="center" py={4}>
                          No expense data.
                        </Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>%</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {categories.map((row, i) => {
                                const pct = totalSpend > 0
                                  ? (parseFloat(row.total) / totalSpend * 100).toFixed(1)
                                  : 0;
                                const isSelected = drillCategory === row.category;
                                return (
                                  <TableRow
                                    key={i}
                                    hover
                                    onClick={() => handleDrillDown(row.category)}
                                    sx={{
                                      cursor: 'pointer',
                                      bgcolor: isSelected ? 'action.selected' : 'inherit',
                                    }}
                                  >
                                    <TableCell>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Box sx={{
                                          width: 10, height: 10, borderRadius: '50%',
                                          bgcolor: getCategoryColor(row.category, i),
                                        }} />
                                        {row.category || 'Uncategorized'}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">{fmtCurrency(row.total)}</TableCell>
                                    <TableCell align="right">{pct}%</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Drilled-down transactions */}
                {drillCategory && (
                  <Grid item xs={12}>
                    <Card elevation={2}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="h6" fontWeight={600}>
                            {drillCategory} — Transactions
                          </Typography>
                          <Button size="small" onClick={clearDrill}>Clear</Button>
                        </Box>
                        {drillLoading ? (
                          <Box textAlign="center" py={2}><CircularProgress size={24} /></Box>
                        ) : drillTxns.length === 0 ? (
                          <Typography color="text.secondary">
                            No transactions found for this category in the selected period.
                          </Typography>
                        ) : (
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {drillTxns.map(tx => (
                                  <TableRow key={tx.id} hover>
                                    <TableCell>{fmtDate(tx.date)}</TableCell>
                                    <TableCell>{tx.description}</TableCell>
                                    <TableCell align="right"
                                      sx={{ fontWeight: 600, color: 'error.main' }}>
                                      -{fmtCurrency(Math.abs(tx.amount))}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}
          </>
        )}

        {/* Custom date range dialog */}
        <Dialog open={customOpen} onClose={() => setCustomOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Custom Date Range</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField
                  label="From"
                  type="date"
                  fullWidth
                  size="small"
                  value={customRange.from}
                  onChange={(e) => setCustomRange(p => ({ ...p, from: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="To"
                  type="date"
                  fullWidth
                  size="small"
                  value={customRange.to}
                  onChange={(e) => setCustomRange(p => ({ ...p, to: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setCustomOpen(false); setPeriod('6m'); }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => {
              setCustomOpen(false);
              setPeriod('custom');
              setDrillCategory(null);
            }}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
